import React, { useCallback, useEffect, useState } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
  Panel
} from 'reactflow';
import { Play, Save, Trash2, Settings } from 'lucide-react';
import toast from 'react-hot-toast';

import { useProjectStore } from '../stores/projectStore';
import { useExecutionStore } from '../stores/executionStore';
import AiNode from './nodes/AiNode';
import NodeConfigModal from './NodeConfigModal';

import 'reactflow/dist/style.css';

const nodeTypes = {
  aiNode: AiNode,
};

const defaultEdgeOptions = {
  animated: true,
  markerEnd: {
    type: MarkerType.ArrowClosed,
    color: '#374151',
  },
  style: {
    strokeWidth: 2,
    stroke: '#374151',
  },
};

const WorkflowEditor = ({ onDragOver, onDrop }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);

  const {
    currentProject,
    nodes: projectNodes,
    edges: projectEdges,
    saveProject,
    updateNodePosition,
    deleteNode,
    updateNode
  } = useProjectStore();

  const { executeNode, executions } = useExecutionStore();

  // プロジェクトのノード・エッジを同期
  useEffect(() => {
    if (currentProject) {
      // ProjectStoreのノードを設定し、選択状態を保持
      setNodes(projectNodes.map(node => ({
        ...node,
        selected: selectedNode?.id === node.id
      })));
      setEdges(projectEdges);
    }
  }, [currentProject, projectNodes, projectEdges, selectedNode, setNodes, setEdges]);

  // エッジ接続
  const onConnect = useCallback(
    (params) => {
      const newEdge = {
        ...params,
        id: `edge_${params.source}_${params.target}`,
        animated: true,
        markerEnd: {
          type: MarkerType.ArrowClosed,
        },
      };
      setEdges((eds) => addEdge(newEdge, eds));
      
      // プロジェクトストアに保存
      const { addEdge: addProjectEdge } = useProjectStore.getState();
      addProjectEdge(newEdge);
    },
    [setEdges]
  );

  // ノード位置変更
  const onNodeDragStop = useCallback(
    (event, node) => {
      updateNodePosition(node.id, node.position);
    },
    [updateNodePosition]
  );

  // ノード選択
  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
    
    // 全ノードの選択状態をリセットし、クリックされたノードのみを選択
    setNodes((nds) => 
      nds.map((n) => ({
        ...n,
        selected: n.id === node.id
      }))
    );
  }, [setNodes]);

  // ノード削除
  const handleDeleteNode = useCallback(() => {
    if (selectedNode) {
      deleteNode(selectedNode.id);
      setSelectedNode(null);
      // 全ノードの選択状態を解除
      setNodes((nds) => nds.map((n) => ({ ...n, selected: false })));
      toast.success('ノードを削除しました');
    }
  }, [selectedNode, deleteNode, setNodes]);

  // ノード設定
  const handleConfigNode = useCallback(() => {
    if (selectedNode) {
      setShowConfigModal(true);
    }
  }, [selectedNode]);

  // ノード実行
  const handleExecuteNode = useCallback(async () => {
    if (!selectedNode) return;

    try {
      const nodeData = selectedNode.data;
      const jobId = await executeNode(nodeData.nodeType, nodeData.inputs);
      
      // ノードの実行状態を更新
      updateNode(selectedNode.id, {
        executionStatus: 'running',
        lastJobId: jobId
      });
      
      toast.success(`ノード "${nodeData.label}" の実行を開始しました`);
    } catch (error) {
      toast.error(`実行エラー: ${error.message}`);
    }
  }, [selectedNode, executeNode, updateNode]);

  // プロジェクト保存
  const handleSave = useCallback(() => {
    saveProject();
    toast.success('プロジェクトを保存しました');
  }, [saveProject]);

  // ワークフロー全体実行
  const handleExecuteWorkflow = useCallback(async () => {
    try {
      if (!currentProject) {
        toast.error('プロジェクトを選択してください');
        return;
      }

      if (projectNodes.length === 0) {
        toast.error('実行するノードがありません');
        return;
      }

      // ノードをオブジェクト形式に変換
      const nodesObj = {};
      projectNodes.forEach(node => {
        nodesObj[node.id] = node;
      });

      // ワークフロー情報を構築
      const workflow = {
        name: currentProject.name,
        edges: projectEdges
      };

      // 実行
      const { executeWorkflow } = useExecutionStore.getState();
      const jobId = await executeWorkflow(workflow, nodesObj, {});
      
      toast.success(`ワークフロー実行開始: ${jobId}`);
    } catch (error) {
      toast.error(`ワークフロー実行エラー: ${error.message}`);
    }
  }, [currentProject, projectNodes, projectEdges]);

  // キーボードショートカット
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.ctrlKey || event.metaKey) {
        switch (event.key) {
          case 's':
            event.preventDefault();
            handleSave();
            break;
          case 'Delete':
          case 'Backspace':
            if (selectedNode) {
              event.preventDefault();
              handleDeleteNode();
            }
            break;
          default:
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave, handleDeleteNode, selectedNode]);

  // 実行状態の反映
  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) => {
        const execution = Object.values(executions).find(
          (exec) => exec.jobId === node.data.lastJobId
        );
        
        if (execution) {
          return {
            ...node,
            data: {
              ...node.data,
              executionStatus: execution.status,
              progress: execution.progress,
              result: execution.result,
              error: execution.error
            }
          };
        }
        
        return node;
      })
    );
  }, [executions, setNodes]);

  if (!currentProject) {
    return null;
  }

  return (
    <div className="h-full w-full">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onNodeDragStop={onNodeDragStop}
        onNodeClick={onNodeClick}
        onPaneClick={() => {
          setSelectedNode(null);
          // 全ノードの選択状態を解除
          setNodes((nds) => nds.map((n) => ({ ...n, selected: false })));
        }}
        nodeTypes={nodeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        fitView
        className="bg-gray-50"
      >
        {/* ツールバー */}
        <Panel position="top-left" className="flex space-x-2">
          <button
            onClick={handleSave}
            className="flex items-center space-x-1 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            title="保存 (Ctrl+S)"
          >
            <Save size={16} />
            <span>保存</span>
          </button>
          
          <button
            onClick={handleExecuteWorkflow}
            className="flex items-center space-x-1 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            title="ワークフロー実行"
          >
            <Play size={16} />
            <span>実行</span>
          </button>
        </Panel>

        {/* 選択ノードツールバー */}
        {selectedNode && (
          <Panel position="top-right" className="flex space-x-2">
            <div className="bg-white rounded-lg shadow-lg p-2 flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-700">
                {selectedNode.data.label}
              </span>
              
              <button
                onClick={handleConfigNode}
                className="p-1 text-gray-600 hover:text-blue-600 transition-colors"
                title="設定"
              >
                <Settings size={16} />
              </button>
              
              <button
                onClick={handleExecuteNode}
                className="p-1 text-gray-600 hover:text-green-600 transition-colors"
                title="このノードを実行"
              >
                <Play size={16} />
              </button>
              
              <button
                onClick={handleDeleteNode}
                className="p-1 text-gray-600 hover:text-red-600 transition-colors"
                title="削除"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </Panel>
        )}

        {/* プロジェクト情報 */}
        <Panel position="bottom-left">
          <div className="bg-white rounded-lg shadow-lg p-3">
            <h3 className="font-medium text-gray-800">{currentProject.name}</h3>
            <p className="text-sm text-gray-600">
              ノード: {nodes.length} | エッジ: {edges.length}
            </p>
          </div>
        </Panel>

        {/* ミニマップ */}
        <MiniMap
          nodeColor={(node) => {
            const category = node.data.config?.category || 'default';
            const colors = {
              text: '#10b981',
              audio: '#f59e0b', 
              ai: '#8b5cf6',
              storage: '#ef4444',
              logic: '#6b7280',
              default: '#9ca3af'
            };
            return colors[category] || colors.default;
          }}
          className="bg-white border border-gray-200 rounded-lg"
        />

        {/* コントロール */}
        <Controls className="bg-white border border-gray-200 rounded-lg" />

        {/* 背景 */}
        <Background variant="dots" gap={20} size={1} color="#e5e7eb" />
      </ReactFlow>

      {/* ノード設定モーダル */}
      {showConfigModal && selectedNode && (
        <NodeConfigModal
          isOpen={showConfigModal}
          nodeData={selectedNode}
          onClose={() => setShowConfigModal(false)}
          onSave={(nodeId, updates) => {
            updateNode(nodeId, updates);
            setShowConfigModal(false);
            toast.success('ノード設定を更新しました');
          }}
        />
      )}
    </div>
  );
};

export default WorkflowEditor;